import { createContext } from 'react';

// Complex context definitions that are too large for the main contexts file

// CatalogContext types and context
export interface CatalogItem {
  userId: string;
  catalogItemId: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED' | 'DESTROYED';
  createdAt: string;
  updatedAt: string;
  visibility: 'public' | 'private';
  coverImageId?: string;
  aliCatalogNumber?: string;
  isLastCatalogItem?: boolean;
  images: Array<{
    imageId: string;
    filename: string;
    size: number;
    mimeType: string;
    s3Key: string;
    status: string;
    url?: string;
    uploadId?: string;
    visibility?: 'public' | 'private';
    uploadedAt?: string | null;
    renditions?: Array<{
      size: string;
      format: string;
      s3Key: string;
      url?: string;
      dimensions: { width: number; height: number };
    }>;
  }>;
}

interface CatalogContextType {
  createCatalogItem: (data: Partial<CatalogItem>) => Promise<string>;
  getCatalogItem: (catalogItemId: string, ownerId?: string | null) => Promise<CatalogItem>;
  getRelatedItemThumbnail: (catalogItemId: string) => Promise<CatalogItem>;
  updateCatalogItem: (catalogItemId: string, data: Partial<CatalogItem>) => Promise<void>;
  getAllCatalogItems: (
    ownerId?: string | null,
    catalogAliId?: string | null,
  ) => Promise<CatalogItem[]>;
  saveDescription: (catalogItemId: string, data: unknown, ownerId?: string | null) => Promise<void>;
  getDescription: (catalogItemId: string, ownerId?: string | null) => Promise<unknown>;
  saveMeasurements: (catalogItemId: string, data: unknown, ownerId?: string | null) => Promise<void>;
  getMeasurements: (catalogItemId: string, ownerId?: string | null) => Promise<unknown>;
  saveKeywords: (catalogItemId: string, data: unknown, ownerId?: string | null, catalogAliId?: string | null) => Promise<{ aliCatalogNumber?: string }>;
  getKeywords: (catalogItemId: string, ownerId?: string | null) => Promise<unknown>;
  saveExhibitions: (catalogItemId: string, data: unknown[], stepNotes?: Record<string, string[]>, ownerId?: string | null) => Promise<void>;
  getExhibitions: (catalogItemId: string, ownerId?: string | null) => Promise<{ exhibitions: unknown[]; stepNotes: Record<string, string[]> }>;
  saveAdditionalInfo: (catalogItemId: string, data: unknown, ownerId?: string | null) => Promise<void>;
  getAdditionalInfo: (catalogItemId: string, ownerId?: string | null) => Promise<unknown>;
  getProvenanceChain: (catalogItemId: string, ownerId?: string | null) => Promise<{ records: unknown[]; version: number; chainLength: number }>;
  addProvenanceRecord: (catalogItemId: string, data: unknown, ownerId?: string | null) => Promise<void>;
  commitProvenanceRecords: (catalogItemId: string, records: Array<{ publicRendering: string; internalRendering: string; order: number; isPublic?: boolean }>, ownerId?: string | null) => Promise<{ committedRecords: unknown[]; chainLength: number }>;
  replaceProvenanceRecords: (catalogItemId: string, records: Array<{ recordId?: string; publicRendering: string; internalRendering: string; order: number; isPublic?: boolean }>, expectedVersion?: number, ownerId?: string | null) => Promise<{ records: unknown[]; chainLength: number; version: number }>;
  catalogItems: CatalogItem[];
  currentItem: CatalogItem | null;
  isLoading: boolean;
  error: string | null;
  setCurrentItem: (item: CatalogItem | null) => void;
}

export const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

// PermissionsContext
interface PermissionsContextType {
  // userId (Cognito sub) and assignedId (own ALI) are carried by the runtime
  // UserPermissions value (PermissionsContext.tsx) and used for ownership checks
  // and own-catalog album badges; surface them here so consumers reading this
  // context directly are typed for them.
  permissions: { role?: string; userId?: string; assignedId?: string } | null;
  isLoading: boolean;
  hasPermission: (action: string, resource?: Record<string, unknown>) => boolean;
  canViewCatalog: (catalogId: string, visibility: 'public' | 'private') => boolean;
  canEditCatalog: (catalogId: string, ownerId: string) => boolean;
  canBulkOperation: (itemCount: number) => boolean;
  refreshPermissions: () => Promise<void>;
}

export const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// SearchContext
import type { SearchFilters, SearchResult, SearchFacets, SortBy, FacetBucket } from './SearchContext';

interface SearchContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  search: (query: string, filters?: SearchFilters) => Promise<SearchResult[]>;
  suggest: (query: string, field: string) => Promise<string[]>;
  suggestCombined: (query: string) => Promise<string[]>;
  searchResults: SearchResult[];
  suggestions: Record<string, string[]>;
  facets: SearchFacets | null;
  /**
   * Facet buckets parsed from the backend response when the caller opted in
   * via `filters.requestedFacets`. Null when no facets were requested or the
   * response didn't include them.
   */
  facetBuckets: Record<string, FacetBucket[]> | null;
  isSearching: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  sortBy: SortBy;
  setSortBy: (sort: SortBy) => void;
}

export const SearchContext = createContext<SearchContextType | undefined>(undefined);

// UploadContext
interface UploadContextType {
  catalogId: string | null;
  catalogTitle: string;
  catalogDescription: string;
  uploadedImages: unknown[];
  selectedCoverId: string | null;
  aliCatalogNumber: string | null;
  /**
   * Title derived from the primary image filename on the very first upload to a
   * brand-new object (#1225). Set once in UploadTab when uploadedImages is empty
   * and no catalogId exists yet.  DescribeTab reads this to auto-populate the
   * Title field when the loaded title is empty and no catalogItemId prop is set
   * (i.e. create-wizard flow, not edit mode).
   */
  suggestedTitle: string;
  setCatalogId: (id: string | null) => void;
  setCatalogTitle: (title: string) => void;
  setCatalogDescription: (description: string) => void;
  setUploadedImages: (images: unknown[]) => void;
  setSelectedCoverId: (imageId: string | null) => void;
  setAliCatalogNumber: (number: string | null) => void;
  setSuggestedTitle: (title: string) => void;
  addImage: (image: unknown) => void;
  updateImageStatus: (imageId: string, status: unknown, thumbnailUrl?: string) => void;
  updateImageMetadata: (imageId: string, patch: { filename?: string; width?: number; height?: number; fileSize?: number; progress?: number }) => void;
  updateImageVisibility: (imageId: string, visibility: 'public' | 'private') => void;
  reorderImages: (orderedImageIds: string[]) => void;
  getCoverImage: () => unknown | null;
  createCatalog: (title: string, ownerId?: string | null, description?: string) => Promise<string>;
}

export const UploadContext = createContext<UploadContextType | undefined>(undefined);

// CollectionsContext
import { CollectionsContextType } from '../types/collection';

export const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

// WizardCompletionContext
export interface TabProgress {
  completed: number;
  total: number;
}

export interface WizardCompletionContextValue {
  markStepComplete: (tab: string, stepIndex: number, completed: boolean) => void;
  batchUpdateTab: (tab: string, steps: Record<number, boolean>) => void;
  bulkInitialize: (tabsData: Record<string, Record<number, boolean>>) => void;
  isStepComplete: (tab: string, stepIndex: number) => boolean;
  getTabProgress: (tab: string) => TabProgress;
  resetCompletion: (catalogId: string) => void;
}

export const WizardCompletionContext = createContext<WizardCompletionContextValue | undefined>(undefined);

// SignupWizardContext - Multi-step signup flow state management
import type { BillingPeriod } from '../utils/paymentTiers';
export type SignupTier = 'viewer' | 'creator' | 'commercial' | 'delegate' | null;
export type SignupStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type SignupStepName = 'contact' | 'verify-email' | 'select-plan' | 'account-details' | 'payment' | 'password' | 'mfa';
export type MfaMethod = 'totp' | 'sms' | null;

export interface SignupWizardState {
  step: SignupStep;
  signupToken: string | null;
  cognitoUsername: string | null;  // Cognito username when using Cognito verification flow
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;  // Public name for artwork attribution (auto-suggested from first+last name)
  initials: string;  // 2-letter initials for ALI ID generation
  organization?: string;
  businessName?: string;  // Required for commercial tier
  businessType?: string;  // Required for commercial tier
  emailVerified: boolean;
  selectedTier: SignupTier;
  billingPeriod: BillingPeriod;
  paymentComplete: boolean;
  passwordSet: boolean;
  mfaConfigured: boolean;
  mfaMethod: MfaMethod;  // Track which MFA method was configured (for success page messaging)
  isLoading: boolean;
  error: string | null;
}

export interface SignupWizardContextValue {
  state: SignupWizardState;
  setStep: (step: SignupStep) => void;
  setContactInfo: (info: { email?: string; firstName?: string; lastName?: string; displayName?: string; initials?: string; organization?: string; businessName?: string; businessType?: string }) => void;
  setSignupToken: (token: string | null) => void;
  setCognitoUsername: (username: string | null) => void;
  setEmailVerified: (verified: boolean) => void;
  setSelectedTier: (tier: SignupTier) => void;
  setBillingPeriod: (period: BillingPeriod) => void;
  setPaymentComplete: (complete: boolean) => void;
  setPasswordSet: (set: boolean) => void;
  setMfaConfigured: (configured: boolean) => void;
  setMfaMethod: (method: MfaMethod) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  resetWizard: () => void;
  abandonAndReset: () => Promise<boolean>;
  canProceedToStep: (step: SignupStep) => boolean;
  isResumingSession: boolean;
  isPaidTier: boolean;
  requiresMfa: boolean;
  currentStepName: SignupStepName;
}

export const SignupWizardContext = createContext<SignupWizardContextValue | undefined>(undefined);
